import { Moment } from 'moment';
import {
	toMoment,
	formatDate,
} from '@shared/utils/date';
import {
	HIIFieldTypes,
	DATE_AND_TIME_DMY,
	PnlGroups,
	MillisecondsPer,
	InvoiceTemplates,
	HireUnit,
	TimeZoneFormat,
} from '@shared/utils/constants';
import type { HIIOffHirePeriodType } from '@api/models/hii-off-hire-period';
import type HireInvoiceItemModel from '@api/models/hire-invoice-item';
import { round } from '../utils/math';
import { formatCurrency } from '../utils/currency';
import TemplateItem from '../TemplateItem';
import getFirstNotNull from '../utils/getFirstNotNull';
import HireInvoiceItem, { HIIConstructorParams } from './HireInvoiceItem';

const { UTC, LocalTime } = TimeZoneFormat;

class HIIOffHirePeriod extends HireInvoiceItem {
	static get itemType() {
		return 'offHirePeriod';
	}

	voyageOffHirePeriodId: number;
	hireDaysDecimals: number | null | undefined;
	hirePerDay: number;
	startTime: Moment;
	endTime: Moment;
	invoiceNote: string | null | undefined;
	note: string | null | undefined;
	type: string | null | undefined;
	percentageForOwnersAccount: number;
	useUTC: boolean;
	hireUnit?: HireUnit;

	constructor(
		id: number,
		isOriginal: boolean,
		params: HIIConstructorParams & {
			voyageOffHirePeriodId: number;
			hireDaysDecimals?: number | null;
			hirePerDay: number;
			startTime: Moment | Date | string;
			endTime: Moment | Date | string;
			invoiceNote?: string | null;
			note?: string | null;
			type?: string | null;
			percentageForOwnersAccount: number;
			useUTC?: boolean;
		},
	) {
		super(id, isOriginal, { fixtureCurrency: params.fixtureCurrency });

		this.voyageOffHirePeriodId = params.voyageOffHirePeriodId;
		this.hireDaysDecimals = params.hireDaysDecimals;
		this.hirePerDay = params.hirePerDay;
		this.startTime = toMoment(params.startTime);
		this.endTime = toMoment(params.endTime);
		this.invoiceNote = params.invoiceNote;
		this.note = params.note;
		this.type = params.type;
		this.percentageForOwnersAccount = params.percentageForOwnersAccount;
		this.useUTC = params.useUTC ?? true;

		this._showTotal = true;
		this.itemTypeLabel = 'Off-hire';
		this.addManually = true;
		this.templateSection = TemplateItem.Sections.OFF_HIRE;
		this.pnlGroup = PnlGroups.OFFHIRE;

		this._fields = {
			hirePerDay: {
				type: HIIFieldTypes.CURRENCY,
				label: 'Hire per day',
			},
			startTime: {
				type: HIIFieldTypes.DATE,
				label: 'From',
			},
			endTime: {
				type: HIIFieldTypes.DATE,
				label: 'To',
			},
			percentageForOwnersAccount: {
				type: HIIFieldTypes.PERCENTAGE,
				label: 'Proportion for Owner',
			},
		};
	}

	_calculateTotal(_invoiceItems: HireInvoiceItem[]) {
		const days = this.getDays();
		const reversedHire = round(this.hirePerDay * days);

		return round(-1 * reversedHire * (this.percentageForOwnersAccount / 100), 2);
	}

	getDays() {
		return round(
			toMoment(this.endTime).diff(this.startTime) / MillisecondsPer.DAY, this.hireDaysDecimals ?? 6,
		);
	}

	getDuration(includeTimezone = true) {
		const timeZoneFormat = this.useUTC ? UTC : LocalTime;

		return `
		${formatDate(this.startTime, DATE_AND_TIME_DMY, includeTimezone, timeZoneFormat)} -
			${formatDate(this.endTime, DATE_AND_TIME_DMY, includeTimezone, timeZoneFormat)}
		`;
	}

	getDescription() {
		return `${this.getDuration(true)} (${this.getDays()} days)`;
	}

	static async _getClassParams(model: HIIOffHirePeriodType, _parentModel: HireInvoiceItemModel) {
		const voyageOffHirePeriod = await model.getVoyageOffHirePeriod();
		const offHirePeriod = await voyageOffHirePeriod.getOffHirePeriod();

		return {
			voyageOffHirePeriodId: voyageOffHirePeriod.id,
			hirePerDay: getFirstNotNull(model.hirePerDayOverride, model.hirePerDay, voyageOffHirePeriod.hirePerDay),
			startTime: getFirstNotNull(
				model.startTimeOverride,
				model.startTime,
				offHirePeriod.startTime,
			),
			endTime: getFirstNotNull(
				model.endTimeOverride,
				model.endTime,
				offHirePeriod.endTime,
			),
			invoiceNote: voyageOffHirePeriod.invoiceNote,
			hireDaysDecimals: model.hireDaysDecimals,
			note: voyageOffHirePeriod.note,
			type: getFirstNotNull(model.type, offHirePeriod.type),
			percentageForOwnersAccount: getFirstNotNull(
				model.percentageForOwnersAccountOverride,
				model.percentageForOwnersAccount,
				voyageOffHirePeriod.percentageForOwnersAccount,
			),
			useUTC: model.useUTC,
		};
	}

	async saveModel(model: HIIOffHirePeriodType) {
		await super.saveModel(model);

		model.voyageOffHirePeriodId = this.voyageOffHirePeriodId;
		model.hireDaysDecimals = this.hireDaysDecimals ?? null;
		model.type = this.type ?? null;
		model.hirePerDay = this.hirePerDay;
		model.startTime = this.startTime;
		model.endTime = this.endTime;
		model.percentageForOwnersAccount = this.percentageForOwnersAccount;
		model.useUTC = this.useUTC;

		await model.save();

		if (this.isOriginal) {
			const voyageOffHirePeriod = await model.getVoyageOffHirePeriod();
			const offHirePeriod = await voyageOffHirePeriod.getOffHirePeriod();
			const allVoyageHireInvoicePeriods = await offHirePeriod.getVoyageOffHirePeriods();

			voyageOffHirePeriod.hirePerDay = this.hirePerDay;

			// if the offhire is only associated to 1 contract = update the off hire globally
			if (allVoyageHireInvoicePeriods.length === 1) {
				offHirePeriod.startTime = this.startTime;
				offHirePeriod.endTime = this.endTime;
			}

			// if the offhire is associated to more than 1, we will only update it locally
			model.startTimeOverride = this.startTime;
			model.endTimeOverride = this.endTime;
			voyageOffHirePeriod.percentageForOwnersAccount = this.percentageForOwnersAccount;

			await voyageOffHirePeriod.save();
			await offHirePeriod.save();
			await model.save();
		} else {
			model.hirePerDayOverride = this.hirePerDay;
			model.startTimeOverride = this.startTime;
			model.endTimeOverride = this.endTime;
			model.percentageForOwnersAccountOverride = this.percentageForOwnersAccount;

			await model.save();
		}
	}

	_getTemplateItemParams(invoiceItems: HireInvoiceItem[]) {
		const classicTitle = this.withChangeSuffix('Off-hire period');
		const timeZoneFormat = this.useUTC ? UTC : LocalTime;

		return {
			[InvoiceTemplates.CLASSIC]: {
				hideTotal: true,
				columns: [
					[
						[classicTitle],
						(this.invoiceNote == null || this.invoiceNote === '' ? [] : [{
							content: `Note: ${this.invoiceNote}`,
							italic: true,
						}]),
						(this.percentageForOwnersAccount === 100 ? [] : [{
							content: `Proportion for Owner's Account: ${this.percentageForOwnersAccount}%`,
							small: true,
							italic: true,
						}]),
					],
				],
				children: [
					{
						section: 'Hire',
						columns: [
							[
								['From:', formatDate(this.startTime, DATE_AND_TIME_DMY, true, timeZoneFormat) || ''],
								['To:', formatDate(this.endTime, DATE_AND_TIME_DMY, true, timeZoneFormat) || ''],
							],
							`${this.getDays()} days`,
							`at ${formatCurrency(this.hirePerDay, this.fixtureCurrency)}`,
						],
						total: this.getTotal(invoiceItems, false),
					},
				],
			},
			[InvoiceTemplates.NEO]: {
				hideTotal: false,
				columns: [
					{
						content: [
							[
								{
									content: this.withChangeSuffix(`Off-hire ${this.getDuration()}`),
								},
							],
							...(this.invoiceNote ? [[
								{
									content: `Note: ${this.invoiceNote}`,
									small: true,
									italic: true,
								},
							]] : []),
							...(this.percentageForOwnersAccount === 100 ? [] : [[
								{
									content: `Proportion for Owner's Account: ${this.percentageForOwnersAccount}%`,
									small: true,
									italic: true,
								},
							]]),
						],
					},
					`${this.getDays()}`,
					'days',
					`${formatCurrency(this.hirePerDay, this.fixtureCurrency, { hideSymbol: true })}
					${this.hireUnit === HireUnit.MONTHS ? 'pcm' : ''}`,
				],
			},
		};
	}
}

HireInvoiceItem.addItemClass(HIIOffHirePeriod);

export default HIIOffHirePeriod;
